import React from 'react';
import { Link } from 'react-router-dom';
import { RotateCcw, Search } from 'lucide-react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { AppConfirmDialog } from '../../../components/AppConfirmDialog';
import { AppSelect } from '../../../components/AppSelect';
import {
  SETTINGS_INPUT_CLASS,
  SETTINGS_SELECT_TRIGGER_CLASS,
  SETTINGS_TABLE_HEADER,
} from '../utils/settingsSectionStyles';
import { SettingsCard } from '../components/SettingsCard';

type RecycleBinContext = {
  title: string;
  reference: string | null;
  details: string[];
  parent: { label: string; app_path: string } | null;
  hidden: boolean;
};

type RecycleBinItem = {
  id: string;
  entity_type: string;
  display_name: string;
  deleted_at: string;
  purge_after: string;
  days_left: number;
  deleted_by_name?: string | null;
  tax_retention_class?: string;
  impact_summary?: Record<string, unknown>;
  context?: RecycleBinContext | null;
};

type RecycleBinPage = {
  items?: RecycleBinItem[];
  total?: number;
  error?: string;
};

const ENTITY_LABELS: Record<string, string> = {
  employee: 'Mitarbeiter',
  membership: 'Nutzerzugang',
  lead: 'Projekt',
  contact: 'Kontakt',
  organization: 'Organisation',
  offering: 'Vorgang',
  ausschreibung: 'Ausschreibung',
  inbox_email: 'E-Mail',
  invoice: 'Rechnung',
  activity: 'Termin',
};

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

const TH_CLASS = `px-3 py-2 text-left ${SETTINGS_TABLE_HEADER}`;
const PAGER_BTN =
  'inline-flex h-8 items-center rounded-md border border-app-border bg-app-surface px-3 text-xs font-medium text-app-text-muted hover:bg-app-hover disabled:cursor-not-allowed disabled:opacity-40';

function formatDeletedAt(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function itemTitle(item: RecycleBinItem): string {
  return item.context?.title || item.display_name;
}

function ItemDescription({ item }: { item: RecycleBinItem }) {
  const ctx = item.context;
  const details = ctx?.details ?? [];
  const parent = ctx?.parent;
  const taxRelevant = item.tax_retention_class && item.tax_retention_class !== 'none';
  return (
    <div className="min-w-0">
      <div className="flex min-w-0 items-center gap-1.5">
        {ctx?.reference ? <span className="shrink-0 font-medium tabular-nums text-app-text-muted">{ctx.reference}</span> : null}
        <span className="truncate font-medium text-app-text" title={itemTitle(item)}>
          {itemTitle(item)}
        </span>
      </div>
      {details.length > 0 || taxRelevant || parent ? (
        <div className="mt-0.5 truncate text-app-text-subtle" title={[parent?.label, ...details].filter(Boolean).join(' · ')}>
          {parent ? (
            <>
              <Link to={parent.app_path} className="text-app-text-muted hover:text-app-text hover:underline">
                {parent.label}
              </Link>
              {details.length > 0 ? ' · ' : ''}
            </>
          ) : null}
          {details.join(' · ')}
          {taxRelevant ? `${details.length > 0 || parent ? ' · ' : ''}gesetzlich archivierte Kopie bleibt erhalten` : ''}
        </div>
      ) : null}
    </div>
  );
}

export function RecycleBinSettingsSection() {
  const [items, setItems] = React.useState<RecycleBinItem[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [entityFilter, setEntityFilter] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [restoringId, setRestoringId] = React.useState<string | null>(null);
  const [confirmRestore, setConfirmRestore] = React.useState<RecycleBinItem | null>(null);

  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [search]);

  React.useEffect(() => {
    setPage(1);
  }, [entityFilter, debouncedSearch]);

  const requestSeq = React.useRef(0);
  const load = React.useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String((page - 1) * PAGE_SIZE) });
      if (entityFilter) params.set('entityType', entityFilter);
      if (debouncedSearch) params.set('q', debouncedSearch);
      const res = await fetch(`/api/recycle-bin?${params.toString()}`, { credentials: 'include' });
      const data = (await res.json()) as RecycleBinPage;
      if (!res.ok) throw new Error(data.error || 'Papierkorb konnte nicht geladen werden.');
      if (seq !== requestSeq.current) return;
      setItems(data.items ?? []);
      setTotal(data.total ?? data.items?.length ?? 0);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(err instanceof Error ? err.message : 'Fehler');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [entityFilter, debouncedSearch, page]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  React.useEffect(() => {
    // Restoring the last row of the last page would otherwise leave an empty page.
    if (!loading && page > totalPages) setPage(totalPages);
  }, [loading, page, totalPages]);

  const onRestore = async () => {
    if (!confirmRestore) return;
    setRestoringId(confirmRestore.id);
    try {
      const res = await fetch(`/api/recycle-bin/${encodeURIComponent(confirmRestore.id)}/restore`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(data.error || 'Wiederherstellung fehlgeschlagen.');
      setConfirmRestore(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler');
    } finally {
      setRestoringId(null);
    }
  };

  const filtersActive = Boolean(entityFilter || debouncedSearch);
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, page * PAGE_SIZE);
  const confirmParent = confirmRestore?.context?.parent;

  return (
    <>
      <SettingsCard
        title="Papierkorb"
        description="Gelöschte Geschäftsdaten bleiben 28 Tage wiederherstellbar. Steuerrelevante Belege bleiben zusätzlich im gesetzlichen Archiv."
      >
        <div className="space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative w-full sm:max-w-sm sm:flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Betreff, Nummer, Projekt …"
                aria-label="Papierkorb durchsuchen"
                className={`${SETTINGS_INPUT_CLASS} pl-8`}
              />
            </div>
            <div className="w-full sm:w-44">
              <AppSelect
                id="recycle-bin-filter"
                value={entityFilter}
                onChange={setEntityFilter}
                options={[
                  { value: '', label: 'Alle Typen' },
                  ...Object.entries(ENTITY_LABELS).map(([value, label]) => ({ value, label })),
                ]}
                size="sm"
                triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
              />
            </div>
          </div>

          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          ) : null}

          <div>
            <div className="overflow-x-auto">
              <table className="w-full table-fixed text-xs">
                <thead className="border-b border-app-border">
                  <tr>
                    <th className={`${TH_CLASS} w-28 !pl-0`}>Typ</th>
                    <th className={TH_CLASS}>Eintrag</th>
                    <th className={`${TH_CLASS} hidden w-40 sm:table-cell`}>Gelöscht am</th>
                    <th className={`${TH_CLASS} hidden w-20 text-right sm:table-cell`}>Frist</th>
                    <th className="w-10 py-2 pl-3 pr-0" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-app-border">
                  {loading && items.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-4">
                        <InlineLoadingState label="Papierkorb wird geladen …" className="justify-start text-xs" />
                      </td>
                    </tr>
                  ) : items.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-4 text-center text-sm text-app-text-subtle">
                        {debouncedSearch
                          ? `Keine Treffer für „${debouncedSearch}“.`
                          : filtersActive
                            ? 'Keine Einträge dieses Typs.'
                            : 'Papierkorb ist leer.'}
                      </td>
                    </tr>
                  ) : (
                    items.map((item) => (
                      <tr key={item.id} className={`align-middle ${loading ? 'opacity-60' : ''}`}>
                        <td className="py-2 pl-0 pr-3 text-app-text-muted">{ENTITY_LABELS[item.entity_type] ?? item.entity_type}</td>
                        <td className="px-3 py-2">
                          <ItemDescription item={item} />
                          <div className="mt-0.5 truncate text-app-text-subtle sm:hidden">
                            {formatDeletedAt(item.deleted_at)} · noch {item.days_left} T.
                          </div>
                        </td>
                        <td className="hidden px-3 py-2 sm:table-cell">
                          <div className="tabular-nums text-app-text">{formatDeletedAt(item.deleted_at)}</div>
                          {item.deleted_by_name ? (
                            <div className="mt-0.5 truncate text-app-text-subtle" title={item.deleted_by_name}>
                              von {item.deleted_by_name}
                            </div>
                          ) : null}
                        </td>
                        <td className="hidden px-3 py-2 text-right tabular-nums text-app-text-muted sm:table-cell">
                          {item.days_left} Tag{item.days_left === 1 ? '' : 'e'}
                        </td>
                        <td className="py-2 pl-3 pr-0 text-right">
                          <button
                            type="button"
                            onClick={() => setConfirmRestore(item)}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-app-border bg-app-surface text-app-text-muted hover:bg-app-hover hover:text-app-text"
                            title="Wiederherstellen"
                            aria-label={`${itemTitle(item)} wiederherstellen`}
                          >
                            <RotateCcw className="h-3 w-3" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {total > 0 ? (
              <div className="flex items-center justify-between gap-2 border-t border-app-border py-2">
                <span className="text-xs tabular-nums text-app-text-subtle">
                  {rangeStart.toLocaleString('de-DE')}–{rangeEnd.toLocaleString('de-DE')} von {total.toLocaleString('de-DE')}
                </span>
                {totalPages > 1 ? (
                  <div className="flex items-center gap-1">
                    <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} className={PAGER_BTN}>
                      ← Zurück
                    </button>
                    <span className="px-2 text-xs tabular-nums text-app-text-subtle">
                      Seite {page} / {totalPages}
                    </span>
                    <button
                      type="button"
                      disabled={page >= totalPages || loading}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      className={PAGER_BTN}
                    >
                      Weiter →
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </SettingsCard>

      <AppConfirmDialog
        open={Boolean(confirmRestore)}
        onClose={() => !restoringId && setConfirmRestore(null)}
        title="Eintrag wiederherstellen?"
        description={
          confirmRestore ? (
            <>
              {ENTITY_LABELS[confirmRestore.entity_type] ?? confirmRestore.entity_type}{' '}
              <span className="font-medium text-app-text">
                {[confirmRestore.context?.reference, itemTitle(confirmRestore)].filter(Boolean).join(' · ')}
              </span>
              {confirmParent ? <> aus {confirmParent.label}</> : null} wird wieder im Arbeitsbereich sichtbar.
            </>
          ) : null
        }
        confirmLabel="Wiederherstellen"
        isLoading={Boolean(restoringId)}
        onConfirm={() => void onRestore()}
      />
    </>
  );
}
